"use client";

import { motion, useAnimationFrame, useReducedMotion } from "motion/react";
import { useEffect, useRef } from "react";

import { cn } from "@/lib/utils";

type Point = {
  x: number;
  y: number;
};

export type TubeHandoff = {
  sequence: number;
  phase: "incoming" | "outgoing";
  directionX: number;
  directionY: number;
  startedAt: number;
  durationMs?: number;
};

type TubeInactiveDirection = {
  x: number;
  y: number;
};

type TubeState = {
  color: string;
  colorRgb: [number, number, number];
  width: number;
  points: Point[];
  sampleHead: Point;
  sampleSpacing: number;
  headEase: number;
  followEase: number;
  noiseStrength: number;
  noiseScale: number;
  phase: number;
  spread: number;
  sway: number;
};

type Size = {
  width: number;
  height: number;
  dpr: number;
};

type AnchorBounds = {
  id: string;
  left: number;
  right: number;
  top: number;
  bottom: number;
  width: number;
  height: number;
  centerX: number;
  centerY: number;
  orbitX: number;
  orbitY: number;
};

type TubeRenderer = {
  gl: WebGLRenderingContext;
  program: WebGLProgram;
  buffer: WebGLBuffer;
  vertexData: Float32Array;
  resolutionLocation: WebGLUniformLocation | null;
  widthScaleLocation: WebGLUniformLocation | null;
  alphaScaleLocation: WebGLUniformLocation | null;
  positionLocation: number;
  prevLocation: number;
  nextLocation: number;
  sideLocation: number;
  widthLocation: number;
  colorLocation: number;
  trailLocation: number;
  headProgram: WebGLProgram;
  headBuffer: WebGLBuffer;
  headVertexData: Float32Array;
  headResolutionLocation: WebGLUniformLocation | null;
  headPositionLocation: number;
  headSizeLocation: number;
  headColorLocation: number;
};

const TUBE_COLORS = [
  "#ff3b30",
  "#ff9500",
  "#ffd60a",
  "#32d74b",
  "#0a84ff",
  "#5e5ce6",
  "#bf5af2",
];
const TUBE_COUNT = 16;
const TRAIL_POINTS = 32;
const FLOATS_PER_VERTEX = 12;
const HEAD_FLOATS_PER_VERTEX = 6;
const VERTICES_PER_POINT = 2;
const VERTICES_PER_TUBE = TRAIL_POINTS * VERTICES_PER_POINT;
const TOTAL_VERTICES = TUBE_COUNT * VERTICES_PER_TUBE;
const STRIDE_BYTES = FLOATS_PER_VERTEX * Float32Array.BYTES_PER_ELEMENT;
const HEAD_STRIDE_BYTES = HEAD_FLOATS_PER_VERTEX * Float32Array.BYTES_PER_ELEMENT;
const HALO_PASSES = [
  { widthScale: 1.85, alphaScale: 0.07 },
  { widthScale: 3.15, alphaScale: 0.024 },
] as const;

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const mix = (start: number, end: number, amount: number) =>
  start + (end - start) * amount;

const easeInCubic = (value: number) => value * value * value;

const easeOutCubic = (value: number) => 1 - Math.pow(1 - value, 3);

const fract = (value: number) => value - Math.floor(value);

const seededUnit = (seed: number) =>
  fract(Math.sin(seed * 12.9898) * 43758.5453123);

function hexToRgb(hex: string) {
  const normalized = hex.replace("#", "");
  const value =
    normalized.length === 3
      ? normalized
          .split("")
          .map((character) => `${character}${character}`)
          .join("")
      : normalized;

  return {
    r: Number.parseInt(value.slice(0, 2), 16),
    g: Number.parseInt(value.slice(2, 4), 16),
    b: Number.parseInt(value.slice(4, 6), 16),
  };
}

function interpolateColor(start: string, end: string, amount: number) {
  const from = hexToRgb(start);
  const to = hexToRgb(end);

  const r = Math.round(mix(from.r, to.r, amount));
  const g = Math.round(mix(from.g, to.g, amount));
  const b = Math.round(mix(from.b, to.b, amount));

  return `#${[r, g, b]
    .map((channel) => channel.toString(16).padStart(2, "0"))
    .join("")}`;
}

function distributeColors(colors: string[], count: number) {
  if (count <= 1) {
    return [colors[0]];
  }

  const lastIndex = colors.length - 1;

  return Array.from({ length: count }, (_, index) => {
    const scaled = (index / (count - 1)) * lastIndex;
    const baseIndex = Math.floor(scaled);
    const localAmount = scaled - baseIndex;

    return interpolateColor(
      colors[baseIndex],
      colors[Math.min(baseIndex + 1, lastIndex)],
      localAmount
    );
  });
}

function wobble(seed: number, time: number, x: number, y: number) {
  const waveA = Math.sin(seed + time * 0.9 + x * 0.0021);
  const waveB = Math.cos(seed * 1.37 - time * 1.2 + y * 0.0028);
  const waveC = Math.sin(seed * 0.61 + time * 0.5 + (x + y) * 0.0017);
  return waveA * 0.45 + waveB * 0.35 + waveC * 0.2;
}

function toAnchorBounds(
  containerRect: DOMRect,
  anchorRect: DOMRect,
  id: string
): AnchorBounds {
  const left = anchorRect.left - containerRect.left;
  const top = anchorRect.top - containerRect.top;
  const width = anchorRect.width;
  const height = anchorRect.height;

  return {
    id,
    left,
    right: left + width,
    top,
    bottom: top + height,
    width,
    height,
    centerX: left + width * 0.5,
    centerY: top + height * 0.5,
    orbitX: Math.min(width * 0.18, 110),
    orbitY: Math.min(height * 0.18, 80),
  };
}

function getAnchorBoundsForElement(
  container: HTMLDivElement,
  anchor: HTMLElement,
  index: number
): AnchorBounds | null {
  const anchorRect = anchor.getBoundingClientRect();

  if (anchorRect.width < 1 || anchorRect.height < 1) {
    return null;
  }

  return toAnchorBounds(
    container.getBoundingClientRect(),
    anchorRect,
    anchor.dataset.photonLightbarsAnchor ?? `anchor-${index}`
  );
}

function getActiveAnchorBounds(
  container: HTMLDivElement,
  anchors: HTMLElement[],
  viewportHeight: number
): AnchorBounds | null {
  const containerRect = container.getBoundingClientRect();
  const viewportMidpoint = viewportHeight * 0.5;
  let closestAnchor: AnchorBounds | null = null;
  let closestDistance = Number.POSITIVE_INFINITY;

  anchors.forEach((anchor, index) => {
    const anchorRect = anchor.getBoundingClientRect();

    if (anchorRect.width < 1 || anchorRect.height < 1) {
      return;
    }

    const distance = Math.abs(
      anchorRect.top + anchorRect.height * 0.5 - viewportMidpoint
    );

    if (distance >= closestDistance) {
      return;
    }

    closestDistance = distance;
    closestAnchor = toAnchorBounds(
      containerRect,
      anchorRect,
      anchor.dataset.photonLightbarsAnchor ?? `anchor-${index}`
    );
  });

  return closestAnchor;
}

function createTubes(
  width: number,
  height: number,
  origin: Point = { x: width * 0.68, y: height * 0.5 }
) {
  const minDimension = Math.min(width, height);
  const colors = distributeColors(TUBE_COLORS, TUBE_COUNT);
  const originX = origin.x;
  const originY = origin.y;

  return colors.map((color, index) => {
    const seed = index + 1;
    const spread = mix(-0.42, 0.42, index / Math.max(TUBE_COUNT - 1, 1));
    const angle = mix(Math.PI * 0.68, Math.PI * 1.22, seededUnit(seed * 1.7));
    const widthScale = mix(0.0025, 0.008, seededUnit(seed * 2.3));
    const lengthStep = mix(0.016, 0.03, seededUnit(seed * 2.9)) * minDimension;
    const lateralShift =
      (seededUnit(seed * 3.1) - 0.5) * minDimension * 0.08 +
      spread * width * 0.08;
    const headX = originX + lateralShift;
    const headY = originY;
    const rgb = hexToRgb(color);

    return {
      color,
      colorRgb: [rgb.r / 255, rgb.g / 255, rgb.b / 255] as [
        number,
        number,
        number,
      ],
      width: Math.max(1.5, widthScale * minDimension),
      sampleHead: { x: headX, y: headY },
      sampleSpacing: Math.max(
        8,
        minDimension * mix(0.008, 0.014, seededUnit(seed * 7.1))
      ),
      headEase: mix(0.16, 0.28, seededUnit(seed * 3.7)),
      followEase: mix(0.14, 0.22, seededUnit(seed * 4.1)),
      noiseStrength: mix(0.012, 0.05, seededUnit(seed * 4.7)) * minDimension,
      noiseScale: mix(0.0013, 0.0034, seededUnit(seed * 5.3)),
      phase: seededUnit(seed * 6.1) * Math.PI * 2,
      spread,
      sway: mix(0.6, 1.7, seededUnit(seed * 6.7)),
      points: Array.from({ length: TRAIL_POINTS }, (_, pointIndex) => {
        const distance = pointIndex * lengthStep;
        return {
          x: headX + Math.cos(angle) * distance,
          y: headY + Math.sin(angle) * distance,
        };
      }),
    };
  });
}

function compileShader(
  gl: WebGLRenderingContext,
  type: number,
  source: string
) {
  const shader = gl.createShader(type);

  if (!shader) {
    return null;
  }

  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  if (gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    return shader;
  }

  const shaderType = type === gl.VERTEX_SHADER ? "vertex" : "fragment";
  console.error(
    `[TubesHeroBackground] Failed to compile ${shaderType} shader`,
    gl.getShaderInfoLog(shader)
  );
  gl.deleteShader(shader);
  return null;
}

function linkProgram(
  gl: WebGLRenderingContext,
  vertexShader: WebGLShader,
  fragmentShader: WebGLShader
) {
  const program = gl.createProgram();

  if (!program) {
    return null;
  }

  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);

  if (gl.getProgramParameter(program, gl.LINK_STATUS)) {
    return program;
  }

  console.error(
    "[TubesHeroBackground] Failed to link shader program",
    gl.getProgramInfoLog(program)
  );
  gl.deleteProgram(program);
  return null;
}

function createTubeRenderer(gl: WebGLRenderingContext): TubeRenderer | null {
  const vertexShader = compileShader(
    gl,
    gl.VERTEX_SHADER,
    `
      attribute vec2 aPosition;
      attribute vec2 aPrev;
      attribute vec2 aNext;
      attribute float aSide;
      attribute float aWidth;
      attribute vec3 aColor;
      attribute float aTrail;

      uniform vec2 uResolution;
      uniform float uWidthScale;

      varying float vSide;
      varying float vTrail;
      varying vec3 vColor;

      vec2 safeNormalize(vec2 value) {
        float lengthValue = length(value);

        if (lengthValue < 0.0001) {
          return vec2(1.0, 0.0);
        }

        return value / lengthValue;
      }

      void main() {
        vec2 current = aPosition;
        vec2 dirA = current - aPrev;
        vec2 dirB = aNext - current;

        if (length(dirA) < 0.0001) {
          dirA = dirB;
        }

        if (length(dirB) < 0.0001) {
          dirB = dirA;
        }

        vec2 tangent = safeNormalize(safeNormalize(dirA) + safeNormalize(dirB));
        vec2 normal = vec2(-tangent.y, tangent.x);
        float taper = mix(1.15, 0.12, aTrail);
        vec2 offset = normal * aSide * aWidth * taper * uWidthScale;
        vec2 clip = ((current + offset) / uResolution) * 2.0 - 1.0;

        gl_Position = vec4(clip.x, -clip.y, 0.0, 1.0);
        vSide = aSide;
        vTrail = aTrail;
        vColor = aColor;
      }
    `
  );
  const fragmentShader = compileShader(
    gl,
    gl.FRAGMENT_SHADER,
    `
      precision mediump float;

      uniform float uAlphaScale;

      varying float vSide;
      varying float vTrail;
      varying vec3 vColor;

      void main() {
        float lateral = max(0.0, 1.0 - abs(vSide));
        float glow = pow(lateral, 1.55);
        float core = smoothstep(0.2, 1.0, lateral);
        float trailFade = pow(max(1.0 - vTrail, 0.0), 0.82);
        float headBoost = mix(1.18, 0.82, vTrail);
        float alpha =
          (glow * 0.34 + core * 0.66) * trailFade * headBoost * uAlphaScale;
        vec3 color = mix(vColor, vec3(1.0), pow(core, 3.4) * 0.08);

        if (alpha < 0.004) {
          discard;
        }

        gl_FragColor = vec4(color * alpha, alpha);
      }
    `
  );
  const headVertexShader = compileShader(
    gl,
    gl.VERTEX_SHADER,
    `
      attribute vec2 aPosition;
      attribute float aSize;
      attribute vec3 aColor;

      uniform vec2 uResolution;

      varying vec3 vColor;

      void main() {
        vec2 clip = (aPosition / uResolution) * 2.0 - 1.0;

        gl_Position = vec4(clip.x, -clip.y, 0.0, 1.0);
        gl_PointSize = aSize;
        vColor = aColor;
      }
    `
  );
  const headFragmentShader = compileShader(
    gl,
    gl.FRAGMENT_SHADER,
    `
      precision mediump float;

      varying vec3 vColor;

      void main() {
        vec2 point = gl_PointCoord * 2.0 - 1.0;
        float radius = length(point);
        float glow = smoothstep(1.0, 0.18, radius);
        float core = smoothstep(0.48, 0.0, radius);
        float alpha = glow * 0.34 + core * 0.88;
        vec3 color = mix(vColor, vec3(1.0), pow(core, 3.0) * 0.12);

        if (radius > 1.0 || alpha < 0.01) {
          discard;
        }

        gl_FragColor = vec4(color * alpha, alpha);
      }
    `
  );

  if (!vertexShader || !fragmentShader || !headVertexShader || !headFragmentShader) {
    return null;
  }

  const program = linkProgram(gl, vertexShader, fragmentShader);
  const headProgram = linkProgram(gl, headVertexShader, headFragmentShader);

  gl.deleteShader(vertexShader);
  gl.deleteShader(fragmentShader);
  gl.deleteShader(headVertexShader);
  gl.deleteShader(headFragmentShader);

  if (!program || !headProgram) {
    if (program) {
      gl.deleteProgram(program);
    }
    if (headProgram) {
      gl.deleteProgram(headProgram);
    }
    return null;
  }

  const buffer = gl.createBuffer();
  const headBuffer = gl.createBuffer();

  if (!buffer || !headBuffer) {
    if (buffer) {
      gl.deleteBuffer(buffer);
    }
    if (headBuffer) {
      gl.deleteBuffer(headBuffer);
    }
    gl.deleteProgram(program);
    gl.deleteProgram(headProgram);
    return null;
  }

  const vertexData = new Float32Array(TOTAL_VERTICES * FLOATS_PER_VERTEX);
  const headVertexData = new Float32Array(TUBE_COUNT * HEAD_FLOATS_PER_VERTEX);
  const resolutionLocation = gl.getUniformLocation(program, "uResolution");
  const widthScaleLocation = gl.getUniformLocation(program, "uWidthScale");
  const alphaScaleLocation = gl.getUniformLocation(program, "uAlphaScale");
  const headResolutionLocation = gl.getUniformLocation(headProgram, "uResolution");

  gl.useProgram(program);
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, vertexData.byteLength, gl.DYNAMIC_DRAW);

  const positionLocation = gl.getAttribLocation(program, "aPosition");
  const prevLocation = gl.getAttribLocation(program, "aPrev");
  const nextLocation = gl.getAttribLocation(program, "aNext");
  const sideLocation = gl.getAttribLocation(program, "aSide");
  const widthLocation = gl.getAttribLocation(program, "aWidth");
  const colorLocation = gl.getAttribLocation(program, "aColor");
  const trailLocation = gl.getAttribLocation(program, "aTrail");

  gl.useProgram(headProgram);
  gl.bindBuffer(gl.ARRAY_BUFFER, headBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, headVertexData.byteLength, gl.DYNAMIC_DRAW);

  const headPositionLocation = gl.getAttribLocation(headProgram, "aPosition");
  const headSizeLocation = gl.getAttribLocation(headProgram, "aSize");
  const headColorLocation = gl.getAttribLocation(headProgram, "aColor");

  gl.disable(gl.DEPTH_TEST);
  gl.enable(gl.BLEND);
  gl.blendFuncSeparate(gl.ONE, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
  gl.clearColor(0, 0, 0, 0);

  return {
    gl,
    program,
    buffer,
    vertexData,
    resolutionLocation,
    widthScaleLocation,
    alphaScaleLocation,
    positionLocation,
    prevLocation,
    nextLocation,
    sideLocation,
    widthLocation,
    colorLocation,
    trailLocation,
    headProgram,
    headBuffer,
    headVertexData,
    headResolutionLocation,
    headPositionLocation,
    headSizeLocation,
    headColorLocation,
  };
}

function bindTubeAttributes(renderer: TubeRenderer) {
  const { gl, buffer } = renderer;

  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);

  const defineAttribute = (location: number, size: number, offset: number) => {
    if (location < 0) {
      return;
    }

    gl.enableVertexAttribArray(location);
    gl.vertexAttribPointer(
      location,
      size,
      gl.FLOAT,
      false,
      STRIDE_BYTES,
      offset
    );
  };

  defineAttribute(renderer.positionLocation, 2, 0);
  defineAttribute(renderer.prevLocation, 2, 2 * Float32Array.BYTES_PER_ELEMENT);
  defineAttribute(renderer.nextLocation, 2, 4 * Float32Array.BYTES_PER_ELEMENT);
  defineAttribute(renderer.sideLocation, 1, 6 * Float32Array.BYTES_PER_ELEMENT);
  defineAttribute(renderer.widthLocation, 1, 7 * Float32Array.BYTES_PER_ELEMENT);
  defineAttribute(renderer.colorLocation, 3, 8 * Float32Array.BYTES_PER_ELEMENT);
  defineAttribute(renderer.trailLocation, 1, 11 * Float32Array.BYTES_PER_ELEMENT);
}

function bindHeadAttributes(renderer: TubeRenderer) {
  const { gl, headBuffer } = renderer;

  gl.bindBuffer(gl.ARRAY_BUFFER, headBuffer);

  const defineAttribute = (location: number, size: number, offset: number) => {
    if (location < 0) {
      return;
    }

    gl.enableVertexAttribArray(location);
    gl.vertexAttribPointer(
      location,
      size,
      gl.FLOAT,
      false,
      HEAD_STRIDE_BYTES,
      offset
    );
  };

  defineAttribute(renderer.headPositionLocation, 2, 0);
  defineAttribute(
    renderer.headSizeLocation,
    1,
    2 * Float32Array.BYTES_PER_ELEMENT
  );
  defineAttribute(
    renderer.headColorLocation,
    3,
    3 * Float32Array.BYTES_PER_ELEMENT
  );
}

function destroyTubeRenderer(renderer: TubeRenderer | null) {
  if (!renderer) {
    return;
  }

  renderer.gl.deleteBuffer(renderer.buffer);
  renderer.gl.deleteBuffer(renderer.headBuffer);
  renderer.gl.deleteProgram(renderer.program);
  renderer.gl.deleteProgram(renderer.headProgram);
}

function writeTubeVertices(vertexData: Float32Array, tubes: TubeState[]) {
  let offset = 0;

  tubes.forEach((tube) => {
    const lastIndex = tube.points.length - 1;

    for (let pointIndex = 0; pointIndex < tube.points.length; pointIndex += 1) {
      const current = tube.points[pointIndex];
      const previousBase =
        pointIndex === 0
          ? tube.points[Math.min(1, lastIndex)]
          : tube.points[pointIndex - 1];
      const nextBase =
        pointIndex === lastIndex
          ? tube.points[Math.max(lastIndex - 1, 0)]
          : tube.points[pointIndex + 1];
      const previous =
        pointIndex === 0
          ? {
              x: current.x + (current.x - nextBase.x),
              y: current.y + (current.y - nextBase.y),
            }
          : previousBase;
      const next =
        pointIndex === lastIndex
          ? {
              x: current.x + (current.x - previousBase.x),
              y: current.y + (current.y - previousBase.y),
            }
          : nextBase;
      const trail = pointIndex / Math.max(lastIndex, 1);

      for (const side of [-1, 1] as const) {
        vertexData[offset] = current.x;
        vertexData[offset + 1] = current.y;
        vertexData[offset + 2] = previous.x;
        vertexData[offset + 3] = previous.y;
        vertexData[offset + 4] = next.x;
        vertexData[offset + 5] = next.y;
        vertexData[offset + 6] = side;
        vertexData[offset + 7] = tube.width;
        vertexData[offset + 8] = tube.colorRgb[0];
        vertexData[offset + 9] = tube.colorRgb[1];
        vertexData[offset + 10] = tube.colorRgb[2];
        vertexData[offset + 11] = trail;
        offset += FLOATS_PER_VERTEX;
      }
    }
  });
}

function writeHeadVertices(
  vertexData: Float32Array,
  tubes: TubeState[],
  dpr: number
) {
  let offset = 0;

  tubes.forEach((tube) => {
    const head = tube.points[0];

    vertexData[offset] = head.x;
    vertexData[offset + 1] = head.y;
    vertexData[offset + 2] = Math.max(tube.width * 4.4, 12) * dpr;
    vertexData[offset + 3] = tube.colorRgb[0];
    vertexData[offset + 4] = tube.colorRgb[1];
    vertexData[offset + 5] = tube.colorRgb[2];
    offset += HEAD_FLOATS_PER_VERTEX;
  });
}

function drawTubes(
  renderer: TubeRenderer,
  tubes: TubeState[],
  width: number,
  height: number,
  dpr: number
) {
  const {
    gl,
    program,
    resolutionLocation,
    widthScaleLocation,
    alphaScaleLocation,
    vertexData,
    headProgram,
    headResolutionLocation,
    headVertexData,
  } = renderer;

  writeTubeVertices(vertexData, tubes);
  writeHeadVertices(headVertexData, tubes, dpr);

  gl.viewport(
    0,
    0,
    Math.max(1, Math.floor(width * dpr)),
    Math.max(1, Math.floor(height * dpr))
  );
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.useProgram(program);
  bindTubeAttributes(renderer);
  gl.bufferSubData(gl.ARRAY_BUFFER, 0, vertexData);
  gl.uniform2f(resolutionLocation, width, height);

  HALO_PASSES.forEach(({ widthScale, alphaScale }) => {
    gl.uniform1f(widthScaleLocation, widthScale);
    gl.uniform1f(alphaScaleLocation, alphaScale);

    for (let tubeIndex = 0; tubeIndex < tubes.length; tubeIndex += 1) {
      gl.drawArrays(
        gl.TRIANGLE_STRIP,
        tubeIndex * VERTICES_PER_TUBE,
        VERTICES_PER_TUBE
      );
    }
  });

  gl.uniform1f(widthScaleLocation, 1.0);
  gl.uniform1f(alphaScaleLocation, 1.0);

  for (let tubeIndex = 0; tubeIndex < tubes.length; tubeIndex += 1) {
    gl.drawArrays(
      gl.TRIANGLE_STRIP,
      tubeIndex * VERTICES_PER_TUBE,
      VERTICES_PER_TUBE
    );
  }

  gl.useProgram(headProgram);
  bindHeadAttributes(renderer);
  gl.bufferSubData(gl.ARRAY_BUFFER, 0, headVertexData);
  gl.uniform2f(headResolutionLocation, width, height);
  gl.drawArrays(gl.POINTS, 0, tubes.length);
}

export function TubesHeroBackground({
  active = true,
  activeOriginX = 0.68,
  anchorSelector,
  className,
  enablePointerTracking = true,
  effectOpacity = 1,
  handoff,
  inactiveDirection,
  pauseWhenOffscreen = true,
  renderBaseBackground = true,
}: {
  active?: boolean;
  activeOriginX?: number;
  anchorSelector?: string;
  className?: string;
  enablePointerTracking?: boolean;
  effectOpacity?: number;
  handoff?: TubeHandoff | null;
  inactiveDirection?: TubeInactiveDirection | null;
  pauseWhenOffscreen?: boolean;
  renderBaseBackground?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<TubeRenderer | null>(null);
  const sizeRef = useRef<Size>({ width: 0, height: 0, dpr: 1 });
  const tubesRef = useRef<TubeState[]>([]);
  const anchorElementsRef = useRef<HTMLElement[]>([]);
  const pointerRef = useRef({ x: 0, y: 0, hover: false });
  const targetRef = useRef({ x: 0, y: 0 });
  const isInViewRef = useRef(true);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;

    if (!container || !canvas) {
      return;
    }

    const gl = canvas.getContext("webgl", {
      alpha: true,
      antialias: true,
      depth: false,
      stencil: false,
      premultipliedAlpha: true,
      powerPreference: "high-performance",
    }) as WebGLRenderingContext | null;

    if (!gl) {
      return;
    }

    const renderer = createTubeRenderer(gl);

    if (!renderer) {
      return;
    }

    rendererRef.current = renderer;

    const refreshAnchors = () => {
      const root = container.parentElement;

      if (!root || !anchorSelector) {
        anchorElementsRef.current = [];
        return;
      }

      anchorElementsRef.current = Array.from(
        root.querySelectorAll<HTMLElement>(anchorSelector)
      );
    };

    const setSize = () => {
      const rect = container.getBoundingClientRect();
      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      const dpr = clamp(window.devicePixelRatio || 1, 1, 2);
      const previousSize = sizeRef.current;

      refreshAnchors();

      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      sizeRef.current = { width, height, dpr };

      const initialAnchor =
        anchorSelector && anchorElementsRef.current.length > 0
          ? getAnchorBoundsForElement(container, anchorElementsRef.current[0], 0)
          : null;
      const initialTarget = initialAnchor
        ? { x: initialAnchor.centerX, y: initialAnchor.centerY }
        : { x: width * activeOriginX, y: height * 0.5 };

      if (tubesRef.current.length === 0 || previousSize.width === 0 || previousSize.height === 0) {
        tubesRef.current = createTubes(width, height, initialTarget);
        targetRef.current = initialTarget;
        return;
      }

      const scaleX = width / previousSize.width;
      const scaleY = height / previousSize.height;

      tubesRef.current.forEach((tube) => {
        tube.points.forEach((point) => {
          point.x *= scaleX;
          point.y *= scaleY;
        });
        tube.sampleHead.x *= scaleX;
        tube.sampleHead.y *= scaleY;
      });

      targetRef.current.x *= scaleX;
      targetRef.current.y *= scaleY;
    };

    const observer = new ResizeObserver(setSize);
    observer.observe(container);
    setSize();

    const viewportObserver =
      pauseWhenOffscreen && typeof IntersectionObserver !== "undefined"
        ? new IntersectionObserver(
            ([entry]) => {
              isInViewRef.current = entry?.isIntersecting ?? true;
            },
            {
              rootMargin: "180px 0px",
            }
          )
        : null;

    viewportObserver?.observe(container);

    if (anchorSelector || !enablePointerTracking) {
      pointerRef.current.hover = false;

      return () => {
        observer.disconnect();
        viewportObserver?.disconnect();
        destroyTubeRenderer(rendererRef.current);
        rendererRef.current = null;
      };
    }

    const handlePointerMove = (event: PointerEvent) => {
      const rect = container.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      const hover =
        x >= 0 &&
        x <= rect.width &&
        y >= 0 &&
        y <= rect.height;

      pointerRef.current.hover = hover;

      if (hover) {
        pointerRef.current.x = x;
        pointerRef.current.y = y;
      }
    };

    const handlePointerLeave = () => {
      pointerRef.current.hover = false;
    };

    window.addEventListener("pointermove", handlePointerMove, { passive: true });
    window.addEventListener("pointerleave", handlePointerLeave, {
      passive: true,
    });

    return () => {
      observer.disconnect();
      viewportObserver?.disconnect();
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerleave", handlePointerLeave);
      destroyTubeRenderer(rendererRef.current);
      rendererRef.current = null;
    };
  }, [activeOriginX, anchorSelector, enablePointerTracking, pauseWhenOffscreen]);

  useAnimationFrame((time) => {
    const renderer = rendererRef.current;
    const { width, height, dpr } = sizeRef.current;

    if (!renderer || width === 0 || height === 0 || !isInViewRef.current) {
      return;
    }

    const motionTime = prefersReducedMotion ? 0 : time / 1000;
    const pointer = pointerRef.current;
    const target = targetRef.current;
    let anchoredDock: AnchorBounds | null = null;

    if (anchorSelector && containerRef.current) {
      anchoredDock = getActiveAnchorBounds(
        containerRef.current,
        anchorElementsRef.current,
        window.innerHeight
      );
    }

    if (anchoredDock) {
      const xPadding = Math.max(28, anchoredDock.width * 0.16);
      const yPadding = Math.max(24, anchoredDock.height * 0.16);
      const desiredX = clamp(
        anchoredDock.centerX + Math.cos(motionTime * 0.72) * anchoredDock.orbitX,
        anchoredDock.left + xPadding,
        anchoredDock.right - xPadding
      );
      const desiredY = clamp(
        anchoredDock.centerY + Math.sin(motionTime * 1.04) * anchoredDock.orbitY,
        anchoredDock.top + yPadding,
        anchoredDock.bottom - yPadding
      );

      target.x = desiredX;
      target.y = desiredY;
    } else {
      const isPointerActive = enablePointerTracking && pointer.hover;
      const offscreenX =
        width * 0.5 +
        (inactiveDirection?.x ?? 1) * Math.max(width * 0.82, 96);
      const offscreenY =
        height * 0.5 +
        (inactiveDirection?.y ?? 0) * Math.max(height * 0.82, 96);
      const idleXAmplitude = width * (active ? 0.12 : 0.015);
      const idleYAmplitude = height * (active ? 0.09 : 0.012);
      const idleBaseX = active
        ? width * activeOriginX
        : inactiveDirection
          ? offscreenX
          : width * 0.52;
      const idleBaseY = active
        ? height * 0.5
        : inactiveDirection
          ? offscreenY
          : height * 0.5;
      const idleSpeedX = active ? 0.55 : 0.18;
      const idleSpeedY = active ? 1.1 : 0.24;
      const idleX =
        idleBaseX + Math.cos(motionTime * idleSpeedX) * idleXAmplitude;
      const idleY = idleBaseY + Math.sin(motionTime * idleSpeedY) * idleYAmplitude;
      const desiredX = isPointerActive
        ? clamp(pointer.x, width * 0.04, width * 0.96)
        : idleX;
      const desiredY = isPointerActive
        ? clamp(pointer.y, height * 0.05, height * 0.95)
        : idleY;
      const targetEase = isPointerActive
        ? 0.22
        : active
          ? 0.055
          : inactiveDirection
            ? 0.08
            : 0.03;

      if (handoff) {
        const progress = clamp(
          (performance.now() - handoff.startedAt) / (handoff.durationMs ?? 780),
          0,
          1
        );
        const exitX = width * 0.5 + handoff.directionX * Math.max(width * 0.62, 72);
        const exitY =
          height * 0.5 + handoff.directionY * Math.max(height * 0.62, 72);

        if (handoff.phase === "incoming") {
          const eased = easeOutCubic(progress);
          target.x = mix(exitX, desiredX, eased);
          target.y = mix(exitY, desiredY, eased);
        } else {
          const eased = easeInCubic(progress);
          target.x = mix(desiredX, exitX, eased);
          target.y = mix(desiredY, exitY, eased);
        }
      } else {
        target.x += (desiredX - target.x) * targetEase;
        target.y += (desiredY - target.y) * targetEase;
      }
    }

    const activityScale = handoff ? 1 : active ? 1 : 0.42;
    const spreadWidth =
      (anchoredDock ? anchoredDock.width * 0.34 : width * 0.16) * activityScale;
    const swayAmplitude = anchoredDock
      ? anchoredDock.height * 0.06 * activityScale
      : height * 0.024 * activityScale;
    tubesRef.current.forEach((tube) => {
      const xNoise =
        wobble(
          tube.phase,
          motionTime,
          target.x * tube.noiseScale,
          target.y * tube.noiseScale
        ) * tube.noiseStrength;
      const yNoise =
        wobble(
          tube.phase + 10,
          motionTime * 1.15,
          target.x * tube.noiseScale,
          target.y * tube.noiseScale
        ) *
        tube.noiseStrength *
        0.68;
      const swayY = Math.sin(motionTime * tube.sway + tube.phase) * swayAmplitude;
      const headTargetX = target.x + tube.spread * spreadWidth + xNoise;
      const headTargetY = target.y + swayY + yNoise;
      const currentHead = tube.points[0];

      currentHead.x += (headTargetX - currentHead.x) * tube.headEase;
      currentHead.y += (headTargetY - currentHead.y) * tube.headEase;

      let sampleX = tube.sampleHead.x;
      let sampleY = tube.sampleHead.y;
      let dx = currentHead.x - sampleX;
      let dy = currentHead.y - sampleY;
      let distance = Math.hypot(dx, dy);

      while (distance >= tube.sampleSpacing) {
        const step = tube.sampleSpacing / distance;
        sampleX += dx * step;
        sampleY += dy * step;

        for (
          let pointIndex = tube.points.length - 1;
          pointIndex > 1;
          pointIndex -= 1
        ) {
          tube.points[pointIndex].x = tube.points[pointIndex - 1].x;
          tube.points[pointIndex].y = tube.points[pointIndex - 1].y;
        }

        tube.points[1].x = sampleX;
        tube.points[1].y = sampleY;
        tube.sampleHead.x = sampleX;
        tube.sampleHead.y = sampleY;

        dx = currentHead.x - sampleX;
        dy = currentHead.y - sampleY;
        distance = Math.hypot(dx, dy);
      }
    });

    drawTubes(renderer, tubesRef.current, width, height, dpr);
  });

  return (
    <motion.div
      aria-hidden="true"
      ref={containerRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={cn(
        "pointer-events-none absolute inset-0 z-0 overflow-hidden",
        className
      )}
    >
      <div
        className="absolute inset-0 transition-opacity duration-500"
        style={{ opacity: effectOpacity }}
      >
        {renderBaseBackground ? (
          <div className="absolute inset-0 bg-background" />
        ) : null}
        <canvas ref={canvasRef} className="absolute inset-0 size-full" />
      </div>
    </motion.div>
  );
}
